import { AlertTriangle, Archive, ArrowLeft, CheckCircle, Download, FileText, Image as ImageIcon, Loader2, MapPin, Package, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import leaveApi from '../LeaveManagement/leaveApi';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import { inventoryApi } from './inventoryApi.js';
import { getAllVehicles, getSites } from './inventoryOnboardingApi.js';

const InventoryItemDetail = () => {
  // Log screen view
  useScreenLogging('InventoryItemDetail', {
    component: 'InventoryItemDetail',
    screenType: 'inventory-item-detail',
  });

  const navigate = useNavigate();
  const { itemId } = useParams();
  const { openOverlay } = useOverlay();
  
  const [inventoryItem, setInventoryItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [originalSourceName, setOriginalSourceName] = useState(null);
  const [isSourceLoading, setIsSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState(null);
  const [showMaintenanceOverlay, setShowMaintenanceOverlay] = useState(false);
  const [selectedMaintenanceAction, setSelectedMaintenanceAction] = useState(null);
  const [isQrDownloading, setIsQrDownloading] = useState(false);
  const [qrDownloadError, setQrDownloadError] = useState(null);
  // Get last week date range function (matches Flutter implementation)
  const getLastWeekDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Find last Monday (subtract days since last Monday)
    // weekday: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysSinceMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - daysSinceMonday - 7);
    
    // Last Sunday is 6 days after last Monday
    const lastSunday = new Date(lastMonday);
    lastSunday.setDate(lastMonday.getDate() + 6);
    
    return { start: lastMonday, end: lastSunday };
  };

  // Initialize with last week's date range (matches Flutter app behavior)
  const [selectedDateRange, setSelectedDateRange] = useState(getLastWeekDateRange());
  const [selectedSingleDate, setSelectedSingleDate] = useState(null);
  const [datePickerMode, setDatePickerMode] = useState('range');
  const [showFullData, setShowFullData] = useState(false);

  // Permission state (simplified - can be enhanced later)
  const [permissions] = useState([
    'inventory.details.view_logs',
    'inventory.details.download_qr',
    'inventory.details.set_maintenance_status',
  ]);

  useEffect(() => {
    if (itemId) {
      fetchInventoryItem();
    }
  }, [itemId]);

  useEffect(() => {
    if (inventoryItem) {
      fetchImageUrl();
      fetchOriginalSourceDetails();
    }
  }, [inventoryItem]);

  // Prevent body scroll when maintenance overlay is open
  useEffect(() => {
    if (showMaintenanceOverlay) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [showMaintenanceOverlay]);

  const fetchInventoryItem = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await inventoryApi.getInventoryItem(itemId);

      if (result.success) {
        // Handle API response structure: item may be nested under 'item' key
        const itemData = result.data?.item || result.data;
        setInventoryItem(itemData);
      } else {
        setErrorMessage(result.message || 'Failed to fetch inventory item');
      }
    } catch (error) {
      console.error('Error fetching inventory item:', error);
      setErrorMessage(error.message || 'Failed to fetch inventory item');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchImageUrl = async () => {
    if (!inventoryItem?.picture_url) return;

    try {
      setIsImageLoading(true);
      setImageError(null);

      const pictureUrl = inventoryItem.picture_url;
      console.log('🖼️ Fetching inventory image URL for:', pictureUrl);
      
      // Determine the cache key (use S3 path if available, otherwise use picture_url)
      let cacheKey = null;
      let s3Path = null;
      let finalUrl = null;
      
      // Check if it's already an HTTPS URL (direct S3 URL)
      if (pictureUrl.startsWith('https://')) {
        // Convert HTTPS URL to S3 path format (s3://bucket/path)
        // Format: https://bucket.s3.amazonaws.com/path -> s3://bucket/path
        const urlMatch = pictureUrl.match(/https:\/\/([^/]+)\.s3\.amazonaws\.com\/(.+)/);
        if (urlMatch) {
          const bucket = urlMatch[1];
          const path = urlMatch[2];
          s3Path = `s3://${bucket}/${path}`;
          cacheKey = s3Path;
          console.log('🖼️ Converted HTTPS URL to S3 path:', s3Path);
        } else {
          // If it's a direct HTTPS URL, use it as cache key
          cacheKey = pictureUrl;
        }
      } else if (pictureUrl.startsWith('s3://')) {
        // Already in S3 path format
        s3Path = pictureUrl;
        cacheKey = pictureUrl;
        console.log('🖼️ Using S3 path format:', pictureUrl);
      } else {
        // Unknown format, use picture_url as cache key
        cacheKey = pictureUrl;
        console.log('🖼️ Unknown URL format, using as cache key:', pictureUrl);
      }
      
      // Check cache first (using S3 path as key if available)
      if (cacheKey) {
        const cachedUrl = profileImageCache.get(cacheKey, s3Path ? true : false);
        if (cachedUrl) {
          console.log('✅ Using cached inventory image URL');
          setImageUrl(cachedUrl);
          setIsImageLoading(false);
          return;
        }
      }
      
      // Cache miss - fetch the image URL
      console.log('🔄 Cache miss, fetching inventory image URL');
      
      if (pictureUrl.startsWith('https://')) {
        if (s3Path) {
          // We have an S3 path, get presigned URL
          const presignedUrl = await userService.getS3PresignedUrl(s3Path);
          if (presignedUrl) {
            console.log('✅ Got presigned URL:', presignedUrl);
            // Cache the presigned URL with S3 path as key
            profileImageCache.set(s3Path, presignedUrl, s3Path, true);
            setImageUrl(presignedUrl);
            return;
          }
        }
        
        // If conversion fails, try using the original URL directly with getS3PresignedUrl
        // The backend might accept HTTPS URLs too
        console.log('🔄 Trying to get presigned URL for HTTPS URL directly');
        const presignedUrl = await userService.getS3PresignedUrl(pictureUrl);
        if (presignedUrl) {
          // Cache it
          profileImageCache.set(pictureUrl, presignedUrl);
          setImageUrl(presignedUrl);
          return;
        }
        
        // Fallback: use the HTTPS URL directly (might work if bucket is public or has CORS)
        console.log('⚠️ Using HTTPS URL directly as fallback');
        // Cache the direct URL
        profileImageCache.set(pictureUrl, pictureUrl);
        setImageUrl(pictureUrl);
      } else if (pictureUrl.startsWith('s3://')) {
        // Already in S3 path format
        const presignedUrl = await userService.getS3PresignedUrl(pictureUrl);
        if (presignedUrl) {
          // Cache the presigned URL with S3 path as key
          profileImageCache.set(pictureUrl, presignedUrl, pictureUrl, true);
          setImageUrl(presignedUrl);
        } else {
          throw new Error('Failed to get presigned URL for S3 path');
        }
      } else {
        // Unknown format, try to get presigned URL anyway
        console.log('🔄 Unknown URL format, trying to get presigned URL');
        const presignedUrl = await userService.getS3PresignedUrl(pictureUrl);
        if (presignedUrl) {
          // Cache it
          profileImageCache.set(pictureUrl, presignedUrl);
          setImageUrl(presignedUrl);
        } else {
          throw new Error('Failed to get presigned URL');
        }
      }
    } catch (error) {
      console.error('❌ Error fetching inventory image:', error);
      setImageError(error.message || 'Failed to load image');
    } finally {
      setIsImageLoading(false);
    }
  };

  const fetchOriginalSourceDetails = async () => {
    if (!inventoryItem?.source_type || !inventoryItem?.source_id) return;

    try {
      setIsSourceLoading(true);
      setSourceError(null);

      const sourceType = inventoryItem.source_type.toLowerCase();
      const sourceId = inventoryItem.source_id;

      const sourceName = await fetchEntityName(sourceType, sourceId);
      setOriginalSourceName(sourceName);
    } catch (error) {
      console.error('Error fetching source details:', error);
      setSourceError(error.message || 'Failed to load source details');
    } finally {
      setIsSourceLoading(false);
    }
  };

  const fetchEntityName = async (entityType, entityId) => {
    try {
      switch (entityType.toLowerCase()) {
        case 'user':
        case 'employee':
          const userInfo = await leaveApi.getUserInfo(entityId);
          return userInfo?.user_name || userInfo?.name || userInfo?.employee_id || entityId;
        case 'site':
          const sites = await getSites();
          const site = sites.find(s => s.site_id === entityId);
          return site?.site_name || entityId;
        case 'vehicle':
          const vehicles = await getAllVehicles();
          const vehicle = vehicles.find(v => (v.vehicle_id || v.id) === entityId);
          return vehicle?.registration_no || vehicle?.registration_number || vehicle?.model_name || entityId;
        default:
          return entityId;
      }
    } catch (error) {
      console.error(`Error fetching entity name for ${entityType} ${entityId}:`, error);
      return entityId;
    }
  };

  const getSourceIcon = (sourceType) => {
    switch (sourceType?.toLowerCase()) {
      case 'site':
        return MapPin;
      case 'employee':
        return User;
      case 'vehicle':
        return Package;
      default:
        return Package;
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown Status';

    switch (status.toLowerCase()) {
      case 'good_condition':
        return 'Good Condition';
      case 'under_maintenance':
        return 'Under Maintenance';
      case 'retired':
        return 'Retired';
      default:
        return status
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase())
          .join(' ');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'good_condition':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'under_maintenance':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'retired':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleDownloadQRCode = async () => {
    try {
      setIsQrDownloading(true);
      setQrDownloadError(null);

      const result = await inventoryApi.downloadInventoryItemQR(itemId);

      if (result.success) {
        const qrImageUrl = result.qr_image_url || result.data?.qr_image_url;
        
        if (qrImageUrl) {
          // Open QR code in new tab for download
          window.open(qrImageUrl, '_blank');
          // Show success message
          setTimeout(() => {
            alert('QR code opened in new tab. Right-click the image to save it.');
          }, 500);
        } else {
          throw new Error('QR code URL not found in response');
        }
      } else {
        throw new Error(result.message || 'Failed to download QR code');
      }
    } catch (error) {
      console.error('Error downloading QR code:', error);
      setQrDownloadError(error.message || 'Failed to download QR code');
    } finally {
      setIsQrDownloading(false);
    }
  };

  const showMaintenanceStatusOverlay = (action) => {
    setSelectedMaintenanceAction(action);
    setShowMaintenanceOverlay(true);
  };

  const hideMaintenanceStatusOverlay = () => {
    setShowMaintenanceOverlay(false);
  };

  const confirmStatusChange = () => {
    hideMaintenanceStatusOverlay();
    
    // Navigate to appropriate maintenance page
    if (selectedMaintenanceAction === 'good_condition') {
      // Navigate to ItemMaintenance (Out of Service)
      navigate(`/inventory-item-maintenance/${itemId}`);
    } else {
      // Navigate to ReturnMaintenance (Good Condition)
      navigate(`/inventory-return-maintenance/${itemId}`);
    }
  };

  const handleDateRangeConfirm = (dateResult) => {
    if (dateResult.mode === 'single' && dateResult.singleDate) {
      // Handle single date selection
      setSelectedSingleDate(dateResult.singleDate);
      setSelectedDateRange(null); // Clear date range when single date is selected
      setShowFullData(false); // Clear full data when date is selected
    } else if (dateResult.mode === 'range' && dateResult.startDate && dateResult.endDate) {
      // Handle date range selection
      setSelectedDateRange({
        start: dateResult.startDate,
        end: dateResult.endDate,
      });
      setSelectedSingleDate(null); // Clear single date when range is selected
      setShowFullData(false); // Clear full data when date range is selected
    }
    setDatePickerMode(dateResult.mode); // Remember the selected mode
  };

  const handleOpenDatePicker = () => {
    openOverlay('inventoryDatePicker', {
      mode: datePickerMode,
      startDate: selectedDateRange?.start || getLastWeekDateRange().start,
      endDate: selectedDateRange?.end || getLastWeekDateRange().end,
      singleDate: selectedSingleDate || new Date(),
      onConfirm: handleDateRangeConfirm
    });
  };

  const formatDateRange = () => {
    if (selectedSingleDate) {
      // Format single date: YYYY-MM-DD format
      return selectedSingleDate.toISOString().split('T')[0];
    }
    if (selectedDateRange) {
      // Format date range: YYYY-MM-DD format
      const start = selectedDateRange.start.toISOString().split('T')[0];
      const end = selectedDateRange.end.toISOString().split('T')[0];
      return `${start} to ${end}`;
    }
    return 'Select Date Range';
  };


  const buildInfoRow = (label, value, fullWidth = false) => {
    return (
      <div className={`${fullWidth ? 'col-span-2' : ''} mb-4`}>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
          {label}
        </label>
        <div className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
          {typeof value === 'object' && value !== null ? (
            value
          ) : (
            <p className="text-sm text-gray-900 font-medium">{value || 'N/A'}</p>
          )}
        </div>
      </div>
    );
  };

  const buildSourceDisplay = () => {
    if (isSourceLoading) {
      return (
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading source...</span>
        </div>
      );
    }

    if (sourceError) {
      return (
        <div className="flex items-center gap-2">
          <X className="w-4 h-4 text-red-600" />
          <span className="text-red-600">Error: {sourceError}</span>
        </div>
      );
    }

    if (originalSourceName) {
      const SourceIcon = getSourceIcon(inventoryItem?.source_type);
      return (
        <div className="flex items-center gap-2">
          <SourceIcon className="w-4 h-4 text-blue-600" />
          <span className="text-gray-900 font-medium">{originalSourceName}</span>
        </div>
      );
    }

    return <span className="text-gray-900">{inventoryItem?.source_id || 'N/A'}</span>;
  };

  const buildImageWidget = () => {
    return (
      <div className="w-full">
        <div className="relative w-full aspect-square rounded-2xl border border-gray-200 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 shadow-sm">
          {isImageLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : imageError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 bg-gray-50">
              <X className="w-12 h-12 text-red-400 mb-2" />
              <p className="text-red-600 text-sm mb-3 font-medium">Failed to load image</p>
              <Button
                type="button"
                onClick={fetchImageUrl}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded-md"
              >
                Retry
              </Button>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={inventoryItem?.name || 'Inventory item'}
              className="w-full h-full object-cover"
              onError={() => {
                setImageError('Image failed to load');
              }}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center mb-3">
                <ImageIcon className="w-10 h-10 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm font-medium">No image available</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const buildStatusButton = () => {
    const currentStatus = inventoryItem?.status?.toLowerCase() || 'good_condition';

    if (currentStatus === 'under_maintenance') {
      // Show "Out of service" button
      return (
        <button
          type="button"
          onClick={() => showMaintenanceStatusOverlay('out_of_service')}
          className="w-full p-4 rounded-xl border-2 border-red-300 bg-red-50 hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-full bg-red-600 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-white" />
              </div>
              <span className="text-red-700 font-medium">Out of service</span>
            </div>
            <ArrowLeft className="w-5 h-5 text-gray-500 rotate-180" />
          </div>
        </button>
      );
    } else if (currentStatus === 'good_condition') {
      // Show "Good condition" button
      return (
        <button
          type="button"
          onClick={() => showMaintenanceStatusOverlay('good_condition')}
          className="w-full p-4 rounded-xl border-2 border-green-600 bg-green-50 hover:bg-green-100 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
              <span className="text-green-700 font-medium">Good condition</span>
            </div>
            <ArrowLeft className="w-5 h-5 text-gray-500 rotate-180" />
          </div>
        </button>
      );
    } else {
      // Show "Retired" button (disabled)
      return (
        <div className="w-full p-4 rounded-xl border-2 border-gray-300 bg-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                <Archive className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-600 font-medium">Retired</span>
            </div>
            <ArrowLeft className="w-5 h-5 text-gray-400 rotate-[-90deg]" />
          </div>
        </div>
      );
    }
  };

  const buildMaintenanceStatusOverlay = () => {
    const isGoodCondition = selectedMaintenanceAction === 'good_condition';
    
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="w-full max-w-[464px] max-h-[calc(90vh-2rem)] my-auto relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col">
          <div className="px-8 pt-6 pb-4 flex justify-between items-start flex-shrink-0">
            <div className="flex flex-col">
              <h3 className={typography.h3} style={{ color: '#000000' }}>
                Maintenance Status
              </h3>
            </div>
            {/* Close Button */}
            <button
              type="button"
              onClick={hideMaintenanceStatusOverlay}
              className="text-gray-500 hover:text-gray-700 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Dialog Content */}
          <div className="px-8 flex-1 flex flex-col min-h-0 overflow-y-auto">
            <div className="mb-6">
              <p className="text-gray-700 mb-6">
                {isGoodCondition
                  ? 'Do you want to set the item status as "Out Of Service"?'
                  : 'Do you want to set the item status as "Good Condition"?'}
              </p>
              <div className={`p-6 rounded-xl border-2 ${
                isGoodCondition
                  ? 'bg-red-50 border-red-300'
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isGoodCondition ? 'bg-red-600' : 'bg-green-600'
                  }`}>
                    {isGoodCondition ? (
                      <AlertTriangle className="w-5 h-5 text-white" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <span className={`text-lg font-medium ${
                    isGoodCondition ? 'text-red-700' : 'text-green-700'
                  }`}>
                    {isGoodCondition ? 'Out of service' : 'Good condition'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dialog Actions */}
          <div className="px-8 pt-4 pb-6 flex gap-3 justify-center flex-shrink-0">
            <Button
              type="button"
              onClick={hideMaintenanceStatusOverlay}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              Cancel
            </Button>
            <PrimaryButton
              type="button"
              onClick={confirmStatusChange}
              className="flex-1"
            >
              Yes
            </PrimaryButton>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const hasViewLogsPermission = () => {
    return permissions.includes('inventory.details.view_logs');
  };

  const hasDownloadQrPermission = () => {
    return permissions.includes('inventory.details.download_qr');
  };

  const hasSetMaintenanceStatusPermission = () => {
    return permissions.includes('inventory.details.set_maintenance_status');
  };

  if (isLoading) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading inventory item...</p>
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Inventory Item Detail
            </h1>
            <p className={typography.p} style={{ color: '#6B7280' }}>
              View and manage inventory item details
            </p>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <X className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h3 className={typography.h3} style={{ color: '#DC2626', marginBottom: '0.5rem' }}>
                Error loading inventory item
              </h3>
              <p className="text-red-600 mb-4">{errorMessage}</p>
              <Button
                type="button"
                onClick={fetchInventoryItem}
                className="bg-blue-600 hover:bg-blue-700"
                style={{ color: '#ffffff' }}
              >
                Retry
              </Button>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  if (!inventoryItem) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1
              className={typography.h1}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              Inventory Item Detail
            </h1>
            <p className={typography.p} style={{ color: '#6B7280' }}>
              View and manage inventory item details
            </p>
          </div>
          <Card.Light className="p-12">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className={typography.h3} style={{ color: '#6B7280', marginBottom: '0.5rem' }}>
                Inventory item not found
              </h3>
            </div>
          </Card.Light>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            Inventory Item Detail
          </h1>
          {/* <p className={typography.p} style={{ color: '#6B7280' }}>
            View and manage inventory item details
          </p> */}
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Left Column - Image */}
          <div className="lg:col-span-1">
            <Card.Light className="p-6">
              {buildImageWidget()}
            </Card.Light>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2">
            <Card.Light className="p-6">
              <div className="mb-6 pb-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">General Information</h2>
                {/* <p className="text-sm text-gray-500">Basic details about this inventory item</p> */}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {buildInfoRow('Item Name', inventoryItem.name || 'N/A')}
                {buildInfoRow('Category Type', inventoryItem.type || 'N/A')}
                {buildInfoRow('Source', buildSourceDisplay())}
                {buildInfoRow('Status', (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(inventoryItem.status)}`}>
                    {formatStatus(inventoryItem.status)}
                  </span>
                ))}
                {buildInfoRow('Date of Onboarding', 
                  inventoryItem.date_of_onboarding
                    ? new Date(inventoryItem.date_of_onboarding).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    : 'N/A'
                )}
              </div>

              {(inventoryItem.description || inventoryItem.specifications) && (
                <>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Additional Details</h3>
                    <div className="space-y-4">
                      {inventoryItem.description && buildInfoRow('Description', inventoryItem.description, true)}
                      {inventoryItem.specifications && buildInfoRow('Specifications', inventoryItem.specifications, true)}
                    </div>
                  </div>
                </>
              )}
            </Card.Light>
          </div>
        </div>

        {/* Action Sections - Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* View Logs Section */}
          {hasViewLogsPermission() && (
            <Card.Light className="p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">View Logs</h2>
                <p className="text-sm text-gray-500">Access inventory item activity logs</p>
              </div>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleOpenDatePicker}
                    className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 ${
                      (selectedDateRange || selectedSingleDate)
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-white hover:bg-gray-50 border border-gray-300 text-gray-700'
                    }`}
                    style={(selectedDateRange || selectedSingleDate) ? { color: '#ffffff' } : {}}
                  >
                    <FileText className="w-4 h-4" style={(selectedDateRange || selectedSingleDate) ? { color: '#ffffff' } : {}} />
                    <span style={(selectedDateRange || selectedSingleDate) ? { color: '#ffffff' } : {}}>{formatDateRange()}</span>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const newShowFullData = !showFullData;
                      setShowFullData(newShowFullData);
                      if (newShowFullData) {
                        // Clear date range and single date when showing full data
                        setSelectedDateRange(null);
                        setSelectedSingleDate(null);
                      } else {
                        // Reset to last week's range when turning off full data (matches Flutter)
                        setSelectedDateRange(getLastWeekDateRange());
                        setSelectedSingleDate(null);
                        setDatePickerMode('range');
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 ${
                      showFullData
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                    style={{ color: '#ffffff' }}
                  >
                    <FileText className="w-4 h-4" style={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff' }}>All Data</span>
                  </Button>
                </div>
                {(selectedDateRange || selectedSingleDate) && !showFullData && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-blue-700 text-xs font-medium">
                      {selectedSingleDate 
                        ? `Filtering data for ${formatDateRange()}`
                        : `Filtering data from ${formatDateRange()}`
                      }
                    </p>
                  </div>
                )}
                {showFullData && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-green-700 text-xs font-medium">
                      Showing full data for all time periods
                    </p>
                  </div>
                )}
                <PrimaryButton
                  type="button"
                  onClick={() => navigate(`/inventory-item-detail/${itemId}/logs`)}
                  className="w-full"
                >
                  View Logs
                </PrimaryButton>
              </div>
            </Card.Light>
          )}

          {/* Download QR Section */}
          {hasDownloadQrPermission() && (
            <Card.Light className="p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">QR Code</h2>
                <p className="text-sm text-gray-500">Download QR code for this item</p>
              </div>
              <Button
                type="button"
                onClick={handleDownloadQRCode}
                disabled={isQrDownloading}
                className={`w-full flex items-center justify-center gap-2 py-2.5 ${
                  isQrDownloading
                    ? 'bg-blue-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                style={{ color: '#ffffff' }}
              >
                {isQrDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff' }}>Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" style={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff' }}>Download QR Code</span>
                  </>
                )}
              </Button>
              {qrDownloadError && (
                <p className="mt-2 text-xs text-red-600">{qrDownloadError}</p>
              )}
            </Card.Light>
          )}

        </div>

        {/* Maintenance Status Section - Full Width */}
        {hasSetMaintenanceStatusPermission() && (
          <Card.Light className="p-6 w-full">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Maintenance Status</h2>
              <p className="text-sm text-gray-500">set the maintenance status as "out of service" if the item is damaaged,and set to "Good Condition" if the item has returned from maintenance</p>
            </div>
            {buildStatusButton()}
          </Card.Light>
        )}

        {/* Maintenance Status Overlay */}
        {showMaintenanceOverlay && buildMaintenanceStatusOverlay()}
      </div>
    </div>
  );
};

export default InventoryItemDetail;

